import { z } from 'zod';

import {
  ScheduleStatus,
  ScheduleEventType,
  SchedulePerspective,
} from '../enums';
import { assertPickedDayCoherent, PickedCivilDayDto } from './civilDate';
import { CreateProgramDto } from './program';
import { CreateWorkoutDto } from './workout';
import { isDefined } from '../utils/isDefined';
import { isWireDay } from '../utils/date';

/**
 * THE COVER PAIR IS NULLABLE ON AN ASSIGNED COPY.
 *
 * `CreateWorkoutDto`/`CreateProgramDto` require `coverSource` and `coverType`,
 * which is right for authoring: a workout the user is creating has a cover
 * because the form makes them pick one. It is NOT right for the copies this
 * controller mints, and the mismatch is a permanent lockout rather than a
 * nuisance:
 *
 * `schedule.controller._createProgram`/`_createWorkout` write
 * `coverSource: coverSource ?? undefined` and `coverType: coverSource ? … :
 * undefined`, so when the source row's cover object is definitely absent
 * (`copyContentAssetToOwner`'s documented `onMissingSource: 'drop'`) the copy
 * is stored with BOTH columns NULL — deliberately, so the row cannot advertise
 * a key that resolves to nothing. The schedule edit modal then GETs that row,
 * `mapWorkoutFromApi` passes the nulls through, and `WorkoutEventModal` sends
 * `workout: data.workout` verbatim. The old schema refused null on two fields
 * the UI had no value for, so every later save of that assigned copy answered
 * 422 forever on a field the trainer never touched.
 *
 * Relaxed HERE and not in `CreateWorkoutDto` itself: authoring keeps its
 * guarantee, while the embed can legitimately carry an absent cover. On an
 * update, `null` is also the explicit removal signal; applying it to an already
 * empty assigned copy is idempotent.
 */
const withNullableCover = <T extends z.ZodRawShape>(dto: z.ZodObject<T>) =>
  dto.extend({
    coverSource: dto.shape.coverSource.nullish(),
    coverType: dto.shape.coverType.nullish(),
  });

const ScheduleWorkoutDto = withNullableCover(CreateWorkoutDto);
const ScheduleProgramDto = withNullableCover(CreateProgramDto);

export const UpdateScheduleDto = z
  .object({
    dateTime: z.coerce.date().optional(),
    // D1: the scheduler-PICKED civil day (drag-drop sends the drop cell)
    businessDate: PickedCivilDayDto.optional(),
    firstInCycle: z.boolean().optional(),
    status: z
      .enum([
        ScheduleStatus.PENDING,
        ScheduleStatus.MISSED,
        ScheduleStatus.COMPLETED,
        ScheduleStatus.CANCELLED,
      ])
      .optional(),
    clientId: z.string().uuid().optional(),
    location: z.string().optional().optional(),
    organizationId: z.string().uuid().nullish(),
    comment: z.string().optional(),
    program: ScheduleProgramDto.nullish(),
    workout: ScheduleWorkoutDto.nullish(),
    workoutId: z.string().uuid().nullish(),
    programId: z.string().uuid().nullish(),
  })
  .strict()
  .superRefine((data, ctx) =>
    assertPickedDayCoherent(ctx, data.businessDate, data.dateTime),
  );
export type ApiUpdateScheduleDto = z.infer<typeof UpdateScheduleDto>;

// F12 — `GET /v2/auth/me/schedules` query params.
//
// T0a — this one endpoint serves BOTH "my schedule everywhere" (the personal
// cross-org page) and "my schedule at this organization" (the org self-view),
// because `useSchedule` picks it whenever `clientId === me`. It therefore has
// to carry the scope the caller means, or the org page renders personal and
// foreign-organization rows.
//
// `organizationId` narrows to one organization; omitting it keeps the
// account-wide aggregate the personal page wants. The org-less bucket is not
// addressable here on purpose: an organization page asks for its organization,
// a personal page asks for everything, and no caller needs "personal only"
// yet. When one does, it gets the shared `ProfileDataScope`
// (`organizationIds[]` + `includePersonal`), not a magic literal in this field.
//
// `from`/`to` are ALL-OR-NEITHER. Half a pair cannot express a window, and
// accepting it would silently widen the read back to the full history — the
// defect this pair exists to close. Both bounds take the same `isWireDay`
// contract as every sibling list DTO: a real civil day, or a legacy instant
// from a stale bundle.
export const GetMySchedulesDto = z
  .object({
    from: z
      .string()
      .refine(isWireDay, { message: 'VALIDATION__InvalidDayOrDateTime' })
      .optional(),
    organizationId: z.string().uuid().optional(),
    perspective: z.nativeEnum(SchedulePerspective).optional(),
    to: z
      .string()
      .refine(isWireDay, { message: 'VALIDATION__InvalidDayOrDateTime' })
      .optional(),
  })
  .strict()
  .refine((data) => isDefined(data.from) === isDefined(data.to), {
    message: 'VALIDATION__InvalidDayOrDateTime',
    path: ['to'],
  });
export type ApiGetMySchedulesDto = z.infer<typeof GetMySchedulesDto>;

export const ShareScheduleDto = z
  .object({
    shareTo: z.array(z.string()).min(1, 'VALIDATION__Required'),
  })
  .strict();
export type ApiShareScheduleDto = z.infer<typeof ShareScheduleDto>;

// F-B1: "Save to Library" — forks a scheduled event's workout copy into a
// fresh library row (`clientId: null`) under a caller-chosen title.
export const SaveScheduleToLibraryDto = z
  .object({ title: z.string().trim().min(1).max(255) })
  .strict();
export type ApiSaveScheduleToLibraryDto = z.infer<
  typeof SaveScheduleToLibraryDto
>;

export const CreateScheduleDto = z
  .object({
    dateTime: z.coerce.date(),
    // D1: the civil day the scheduler PICKED — the source of truth for the
    // frozen `businessDate`; the instant alone cannot recover it.
    businessDate: PickedCivilDayDto.optional(),
    program: ScheduleProgramDto.optional().nullable(),
    workout: ScheduleWorkoutDto.optional().nullable(),
    workoutId: z.string().uuid().optional().nullable(),
    programId: z.string().uuid().optional().nullable(),
    eventType: z.enum([ScheduleEventType.PROGRAM, ScheduleEventType.WORKOUT]),
    trainerId: z.string().uuid(),
    organizationId: z.string().uuid().nullish(),
    // At least one target must be provided. `clientId`/`groupId` are the
    // single-target forms kept for back-compat; `clientIds`/`groupIds` let one
    // request fan out to several clients and/or groups (each group is expanded
    // to its active clients on the server, then the whole set is de-duped).
    clientId: z.string().uuid().optional(),
    clientIds: z.array(z.string().uuid()).optional(),
    groupId: z.string().uuid().optional().nullable(),
    groupIds: z.array(z.string().uuid()).optional(),
    location: z.string().optional(),
    comment: z.string().optional(),
    shareTo: z.array(z.string()).optional(),
  })
  .strict()
  .refine(
    (data) =>
      Boolean(data.clientId) ||
      Boolean(data.groupId) ||
      Boolean(data.clientIds?.length) ||
      Boolean(data.groupIds?.length),
    {
      message: 'VALIDATION__Required',
      path: ['clientId'],
    },
  )
  .superRefine((data, ctx) =>
    assertPickedDayCoherent(ctx, data.businessDate, data.dateTime),
  );

export type ApiCreateScheduleDto = z.infer<typeof CreateScheduleDto>;

// The batch endpoint duplicates one full workout (exercises, sets, media) per
// array entry, synchronously inside one transaction. Bounds both the
// transaction's size and the number of tabs the FE batch editor renders —
// kept in @mfw-common (not next to `MAX_SCHEDULE_TARGET_CLIENTS`, which is
// backend-only) because the FE picker caps selection against this same value.
export const MAX_SCHEDULE_BATCH_WORKOUTS = 20;

const ScheduleBatchWorkoutEntryDto = z
  .object({
    workout: ScheduleWorkoutDto,
    // The library/marketplace/premium row this entry's editable content was
    // duplicated from — same role as `CreateScheduleDto.workoutId`: absent
    // for ad-hoc content, present so the controller can re-run the premium
    // gate and read-access check against the SOURCE row rather than trusting
    // the edited payload (which could name premium content unpurchased).
    workoutId: z.string().uuid().optional().nullable(),
    dateTime: z.coerce.date(),
    // D1: same coherence contract as `CreateScheduleDto`/`UpdateScheduleDto` —
    // the scheduler-PICKED civil day for this specific workout's date.
    businessDate: PickedCivilDayDto.optional(),
    location: z.string().optional(),
    comment: z.string().optional(),
  })
  .strict()
  .superRefine((data, ctx) =>
    assertPickedDayCoherent(ctx, data.businessDate, data.dateTime),
  );
export type ApiScheduleBatchWorkoutEntryDto = z.infer<
  typeof ScheduleBatchWorkoutEntryDto
>;

// Batch add — one trainer, one client, N duplicated-and-edited workouts, N
// distinct dates, one atomic write. Workout-only: `eventType`/program
// batching is out of scope, and target fan-out (`clientIds`/`groupIds`/
// `shareTo`) is deliberately absent — this flow targets exactly one client.
export const CreateScheduleWorkoutBatchDto = z
  .object({
    trainerId: z.string().uuid(),
    clientId: z.string().uuid(),
    organizationId: z.string().uuid().nullish(),
    workouts: z
      .array(ScheduleBatchWorkoutEntryDto)
      .min(1, 'VALIDATION__Required')
      .max(MAX_SCHEDULE_BATCH_WORKOUTS, 'VALIDATION__InvalidRange'),
  })
  .strict();
export type ApiCreateScheduleWorkoutBatchDto = z.infer<
  typeof CreateScheduleWorkoutBatchDto
>;
